import { createFileRoute } from "@tanstack/react-router";
import { useServerFn } from "@tanstack/react-start";
import { useQuery, useMutation } from "@tanstack/react-query";
import { useMemo, useState } from "react";
import {
  listOutboundWebhooks,
  listWebhookDeliveries,
  sendCustomTestWebhook,
} from "@/lib/api-keys.functions";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Textarea } from "@/components/ui/textarea";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Send, Copy, CheckCircle2, XCircle, RefreshCw, Webhook, ListChecks } from "lucide-react";
import { Tabs, TabsContent } from "@/components/ui/tabs";
import { PillTabsList, PillTabsTrigger } from "@/components/admin/pill-tabs";
import { toast } from "sonner";

export const Route = createFileRoute("/_authenticated/admin/webhook-tester")({
  component: WebhookTesterPage,
});

const SAMPLES: { label: string; event: string; payload: any }[] = [
  { label: "Ping", event: "test.ping", payload: { message: "hello" } },
  {
    label: "Order created",
    event: "order.created",
    payload: { id: "ord_123", total_cents: 4999, currency: "USD", customer_id: "usr_42" },
  },
  {
    label: "Invoice paid",
    event: "invoice.paid",
    payload: { id: "inv_789", amount_cents: 12000, currency: "USD", paid_at: new Date().toISOString() },
  },
  {
    label: "User signup",
    event: "user.created",
    payload: { id: "usr_new", email: "test@example.com" },
  },
];

function copy(text: string) {
  navigator.clipboard.writeText(text).then(() => toast.success("Copied"));
}

function WebhookTesterPage() {
  const listEps = useServerFn(listOutboundWebhooks);
  const listDel = useServerFn(listWebhookDeliveries);
  const sendFn = useServerFn(sendCustomTestWebhook);

  const epsQ = useQuery({ queryKey: ["wh-endpoints"], queryFn: () => listEps() });

  const [endpointId, setEndpointId] = useState<string>("");
  const [event, setEvent] = useState("test.ping");
  const [payload, setPayload] = useState(JSON.stringify({ message: "hello" }, null, 2));
  const [result, setResult] = useState<any>(null);

  const endpoints = (epsQ.data as any)?.endpoints ?? [];
  const selectedEp = useMemo(
    () => endpoints.find((e: any) => e.id === endpointId),
    [endpoints, endpointId],
  );

  const deliveriesQ = useQuery({
    queryKey: ["wh-tester-deliveries", endpointId],
    queryFn: () => listDel({ data: { endpoint_id: endpointId, limit: 15 } }),
    enabled: !!endpointId,
  });

  const sendMut = useMutation({
    mutationFn: () => sendFn({ data: { endpoint_id: endpointId, event, payload_json: payload } }),
    onSuccess: (r) => {
      setResult(r);
      if (r.ok) toast.success(`Delivered (${r.response.status}) in ${r.latency_ms}ms`);
      else toast.error(`Failed${r.response?.status ? ` (${r.response.status})` : ""}: ${r.error ?? "non-2xx"}`);
      deliveriesQ.refetch();
    },
    onError: (e: any) => toast.error(e?.message ?? "Send failed"),
  });

  const applySample = (s: typeof SAMPLES[number]) => {
    setEvent(s.event);
    setPayload(JSON.stringify(s.payload, null, 2));
  };

  const formatJson = () => {
    try {
      setPayload(JSON.stringify(JSON.parse(payload), null, 2));
    } catch (e: any) {
      toast.error("Invalid JSON");
    }
  };

  return (
    <div className="space-y-6">
      <div>
        <h2 className="text-xl font-semibold tracking-tight flex items-center gap-2">
          <Webhook className="h-5 w-5" /> Webhook tester
        </h2>
        <p className="text-sm text-muted-foreground">
          Send a signed sample event to a chosen endpoint and inspect the response.
        </p>
      </div>

      <Tabs defaultValue="send" className="w-full">
        <PillTabsList search>
          <PillTabsTrigger value="send"><Send className="h-3.5 w-3.5 mr-1.5" />Send event</PillTabsTrigger>
          <PillTabsTrigger value="deliveries"><ListChecks className="h-3.5 w-3.5 mr-1.5" />Recent deliveries</PillTabsTrigger>
        </PillTabsList>

        <TabsContent value="send" className="mt-4">
          <div className="grid gap-6 lg:grid-cols-[1fr_1fr]">
            <Card>
              <CardHeader>
                <CardTitle className="text-base">Request</CardTitle>
                <CardDescription>Choose endpoint, event, and payload. Signed with the endpoint's secret.</CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="space-y-1.5">
                  <Label>Endpoint</Label>
                  <Select value={endpointId} onValueChange={setEndpointId}>
                    <SelectTrigger>
                      <SelectValue placeholder={epsQ.isLoading ? "Loading…" : "Select an endpoint"} />
                    </SelectTrigger>
                    <SelectContent>
                      {endpoints.map((e: any) => (
                        <SelectItem key={e.id} value={e.id}>
                          {e.name} — {e.url}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  {selectedEp && (
                    <div className="flex items-center gap-2 text-xs text-muted-foreground">
                      <Badge variant={selectedEp.is_active ? "default" : "secondary"}>
                        {selectedEp.is_active ? "Active" : "Disabled"}
                      </Badge>
                      <span>Timeout {selectedEp.timeout_ms ?? 10000}ms</span>
                    </div>
                  )}
                </div>

                <div className="space-y-1.5">
                  <Label>Event</Label>
                  <Input value={event} onChange={(e) => setEvent(e.target.value)} placeholder="order.created" />
                </div>

                <div className="space-y-1.5">
                  <Label>Sample payloads</Label>
                  <div className="flex flex-wrap gap-1.5">
                    {SAMPLES.map((s) => (
                      <Button key={s.label} type="button" size="sm" variant="outline" onClick={() => applySample(s)}>
                        {s.label}
                      </Button>
                    ))}
                  </div>
                </div>

                <div className="space-y-1.5">
                  <div className="flex items-center justify-between">
                    <Label>Payload (JSON)</Label>
                    <Button type="button" size="sm" variant="ghost" onClick={formatJson}>
                      Format
                    </Button>
                  </div>
                  <Textarea
                    rows={14}
                    value={payload}
                    onChange={(e) => setPayload(e.target.value)}
                    className="font-mono text-xs"
                    spellCheck={false}
                  />
                </div>

                <Button
                  onClick={() => sendMut.mutate()}
                  disabled={!endpointId || sendMut.isPending}
                  className="w-full"
                >
                  <Send className="h-4 w-4 mr-1.5" />
                  {sendMut.isPending ? "Sending…" : "Send signed event"}
                </Button>
              </CardContent>
            </Card>

            <Card>
              <CardHeader>
                <CardTitle className="text-base flex items-center gap-2">
                  Response
                  {result && (
                    result.ok ? (
                      <Badge className="gap-1"><CheckCircle2 className="h-3 w-3" /> {result.response.status}</Badge>
                    ) : (
                      <Badge variant="destructive" className="gap-1">
                        <XCircle className="h-3 w-3" /> {result.response?.status ?? "ERR"}
                      </Badge>
                    )
                  )}
                  {result && <span className="text-xs font-normal text-muted-foreground">{result.latency_ms}ms</span>}
                </CardTitle>
                <CardDescription>Immediate response from the endpoint.</CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                {!result && (
                  <div className="text-sm text-muted-foreground py-10 text-center border rounded-md">
                    Send an event to see the response here.
                  </div>
                )}
                {result && (
                  <>
                    {result.error && (
                      <div className="rounded-md border border-destructive/30 bg-destructive/10 p-2 text-xs text-destructive">
                        {result.error}
                      </div>
                    )}

                    <div className="space-y-1.5">
                      <div className="flex items-center justify-between">
                        <Label className="text-xs">Signature (sha256)</Label>
                        <Button size="sm" variant="ghost" onClick={() => copy(result.signature)}>
                          <Copy className="h-3 w-3" />
                        </Button>
                      </div>
                      <code className="block break-all rounded bg-muted px-2 py-1.5 text-[11px] font-mono">
                        {result.signature}
                      </code>
                    </div>

                    <div className="space-y-1.5">
                      <Label className="text-xs">Request headers</Label>
                      <pre className="rounded bg-muted px-2 py-1.5 text-[11px] font-mono overflow-auto max-h-32">
{Object.entries(result.request.headers).map(([k, v]) => `${k}: ${v}`).join("\n")}
                      </pre>
                    </div>

                    <div className="space-y-1.5">
                      <Label className="text-xs">Response headers</Label>
                      <pre className="rounded bg-muted px-2 py-1.5 text-[11px] font-mono overflow-auto max-h-32">
{Object.keys(result.response.headers ?? {}).length
  ? Object.entries(result.response.headers).map(([k, v]) => `${k}: ${v}`).join("\n")
  : "(none)"}
                      </pre>
                    </div>

                    <div className="space-y-1.5">
                      <div className="flex items-center justify-between">
                        <Label className="text-xs">Response body</Label>
                        {result.response.body && (
                          <Button size="sm" variant="ghost" onClick={() => copy(result.response.body)}>
                            <Copy className="h-3 w-3" />
                          </Button>
                        )}
                      </div>
                      <pre className="rounded bg-muted px-2 py-1.5 text-[11px] font-mono overflow-auto max-h-64 whitespace-pre-wrap break-all">
{result.response.body ?? "(empty)"}
                      </pre>
                    </div>
                  </>
                )}
              </CardContent>
            </Card>
          </div>
        </TabsContent>

        <TabsContent value="deliveries" className="mt-4">
          <Card>
            <CardHeader className="flex flex-row items-center justify-between space-y-0">
              <div>
                <CardTitle className="text-base">Recent deliveries</CardTitle>
                <CardDescription>
                  {selectedEp ? `Latest 15 attempts to ${selectedEp.name}` : "Select an endpoint in the Send event tab to view logs"}
                </CardDescription>
              </div>
              <Button
                size="sm"
                variant="outline"
                onClick={() => deliveriesQ.refetch()}
                disabled={!endpointId || deliveriesQ.isFetching}
              >
                <RefreshCw className={`h-3.5 w-3.5 mr-1.5 ${deliveriesQ.isFetching ? "animate-spin" : ""}`} />
                Refresh
              </Button>
            </CardHeader>
            <CardContent>
              {!endpointId ? (
                <div className="text-sm text-muted-foreground text-center py-6">No endpoint selected.</div>
              ) : (
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Time</TableHead>
                      <TableHead>Event</TableHead>
                      <TableHead>Status</TableHead>
                      <TableHead>HTTP</TableHead>
                      <TableHead>Attempts</TableHead>
                      <TableHead>Error</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {((deliveriesQ.data as any)?.deliveries ?? []).map((d: any) => (
                      <TableRow key={d.id}>
                        <TableCell className="text-xs whitespace-nowrap">
                          {new Date(d.created_at).toLocaleString()}
                        </TableCell>
                        <TableCell className="text-xs font-mono">{d.event}</TableCell>
                        <TableCell>
                          <Badge variant={d.status === "success" ? "default" : d.status === "pending" ? "secondary" : "destructive"}>
                            {d.status}
                          </Badge>
                        </TableCell>
                        <TableCell className="text-xs">{d.response_status ?? "—"}</TableCell>
                        <TableCell className="text-xs">{d.attempts ?? 0}</TableCell>
                        <TableCell className="text-xs text-muted-foreground max-w-[280px] truncate">
                          {d.error ?? (d.response_body ? String(d.response_body).slice(0, 80) : "—")}
                        </TableCell>
                      </TableRow>
                    ))}
                    {((deliveriesQ.data as any)?.deliveries ?? []).length === 0 && !deliveriesQ.isLoading && (
                      <TableRow>
                        <TableCell colSpan={6} className="text-center text-sm text-muted-foreground py-6">
                          No deliveries yet.
                        </TableCell>
                      </TableRow>
                    )}
                  </TableBody>
                </Table>
              )}
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>
    </div>
  );
}
